import * as Yup from 'yup';
import { formatDateForSubmission } from '@utils/formatDate';

/**
 * Sprint 31, story-160. Field-state/validation/payload-building logic lifted out of
 * `CreateEditProjectModal` so `ProjectWizardPage` can reuse it — same field set, same
 * validation rules, same payload shape, so behavior stays identical between the modal (now
 * edit-only) and the wizard's steps.
 */

// Sprint 31, story-156. `id`/`backendValue` are internal data keys, never rendered — only
// `nameKey` (a `projects.*` catalog key, resolved against `t` inside the caller) is display
// text.
export const PROJECT_STATE_DEFINITIONS = [
  { id: 'ongoing', nameKey: 'statusOngoing' as const, backendValue: 'ONGOING' },
  { id: 'completed', nameKey: 'statusCompleted' as const, backendValue: 'COMPLETED' },
  { id: 'abandoned', nameKey: 'statusAbandoned' as const, backendValue: 'ABANDONED' },
  { id: 'delayed', nameKey: 'statusDelayed' as const, backendValue: 'DELAYED' },
  { id: 'bidding', nameKey: 'statusBidding' as const, backendValue: 'BIDDING' },
  { id: 'draft', nameKey: 'statusDraft' as const, backendValue: 'DRAFT' },
];

export interface ProjectStateOption {
  id: string;
  name: string;
  backendValue: string;
}

export const mapBackendStateToFrontend = (backendState: string): string => {
  const stateMapping: { [key: string]: string } = {
    ONGOING: 'ongoing',
    COMPLETED: 'completed',
    ABANDONED: 'abandoned',
    DELAYED: 'delayed',
    BIDDING: 'bidding',
    DRAFT: 'draft',
  };
  return stateMapping[backendState] || 'ongoing';
};

export interface ProjectFormValues {
  name: string;
  address: string;
  description: string;
  client: string;
  budget: number;
  totalManHours: number | string;
  startDate: string;
  endDate: string;
  lat: string;
  long: string;
  state: string;
  projectManagerId: string;
  superintendentIds: string[];
  reviewerEmployeeIds: string[];
}

/** Field lists used by the wizard's per-step "Next" validation gate. */
export const PROJECT_FORM_STEP_FIELDS = {
  basics: ['name', 'state', 'description'] as (keyof ProjectFormValues)[],
  schedule: ['startDate', 'endDate', 'address'] as (keyof ProjectFormValues)[],
  team: ['projectManagerId', 'superintendentIds', 'reviewerEmployeeIds', 'client'] as (keyof ProjectFormValues)[],
  budget: ['budget', 'totalManHours'] as (keyof ProjectFormValues)[],
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildProjectValidationSchema(t: any) {
  return Yup.object({
    name: Yup.string().required(t.nameRequiredError),
    address: Yup.string().nullable().optional(),
    description: Yup.string().nullable().optional(),
    client: Yup.string().nullable().optional(),
    budget: Yup.number().min(0, t.budgetPositiveError).nullable(),
    totalManHours: Yup.number().min(0, t.totalManHoursPositiveError).nullable(),
    startDate: Yup.string().required(t.startDateRequiredError),
    endDate: Yup.string().required(t.endDateRequiredError),
    state: Yup.string().required(t.stateRequiredError),
  });
}

/** The GraphQL create/edit payload shape (sans `siteImage`/`projectId`, added by the caller). */
export function buildProjectPayload(values: ProjectFormValues, states: ProjectStateOption[]) {
  const selectedState = states.find((state) => state.id === values.state);
  const backendStateValue = selectedState?.backendValue || 'ONGOING';

  return {
    name: values.name,
    address: values.address,
    description: values.description || null,
    clientId: values.client || null,
    budget: values.budget,
    totalManHours:
      values.totalManHours === '' || values.totalManHours === null ? null : Number(values.totalManHours),
    startDate: formatDateForSubmission(values.startDate),
    endDate: formatDateForSubmission(values.endDate),
    lat: values.lat ? parseFloat(values.lat) : null,
    long: values.long ? parseFloat(values.long) : null,
    state: backendStateValue,
    projectManagerId: values.projectManagerId || null,
    superintendentIds: Array.isArray(values.superintendentIds)
      ? values.superintendentIds.filter((id: string) => !!id && id !== '')
      : [],
    reviewerEmployeeIds: Array.isArray(values.reviewerEmployeeIds)
      ? values.reviewerEmployeeIds.filter((id: string) => !!id && id !== '')
      : [],
  };
}

/**
 * Walkthrough fix (sprint 38). What the wizard stores with an AI draft as `projectInput`: the
 * createProject payload (JSON-safe, no site image). The same shape `redesign` stores, so a draft
 * started on one surface resumes on the other.
 */
export function buildProjectInput(values: ProjectFormValues, states: ProjectStateOption[]): Record<string, unknown> {
  return buildProjectPayload(values, states) as unknown as Record<string, unknown>;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : '');
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : []);

/**
 * Inverse of `buildProjectInput`: restores wizard form values from a draft's stored
 * `projectInput`. Unknown or mistyped fields fall back to `base`; never throws.
 */
export function formValuesFromProjectInput(input: Record<string, unknown> | null | undefined, base: ProjectFormValues): ProjectFormValues {
  if (!input || typeof input !== 'object') return base;
  const budget = typeof input.budget === 'number' && Number.isFinite(input.budget) ? input.budget : base.budget;
  const totalManHours = typeof input.totalManHours === 'number' && Number.isFinite(input.totalManHours) ? input.totalManHours : base.totalManHours;
  return {
    ...base,
    name: str(input.name) || base.name,
    address: str(input.address) || base.address,
    description: str(input.description) || base.description,
    client: str(input.clientId) || base.client,
    budget,
    totalManHours,
    startDate: str(input.startDate).slice(0, 10) || base.startDate,
    endDate: str(input.endDate).slice(0, 10) || base.endDate,
    lat: str(input.lat) || base.lat,
    long: str(input.long) || base.long,
    state: typeof input.state === 'string' ? mapBackendStateToFrontend(input.state) : base.state,
    projectManagerId: str(input.projectManagerId) || base.projectManagerId,
    superintendentIds: Array.isArray(input.superintendentIds) ? strList(input.superintendentIds) : base.superintendentIds,
    reviewerEmployeeIds: Array.isArray(input.reviewerEmployeeIds) ? strList(input.reviewerEmployeeIds) : base.reviewerEmployeeIds,
  };
}

const stable = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map(stable);
  if (v && typeof v === 'object') {
    return Object.keys(v as Record<string, unknown>)
      .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = stable((v as Record<string, unknown>)[k]);
        return acc;
      }, {});
  }
  return v;
};

/** Key-order-insensitive equality (Postgres jsonb does not keep key order). */
export function sameProjectInput(a: unknown, b: unknown): boolean {
  return JSON.stringify(stable(a)) === JSON.stringify(stable(b));
}
